import { useEffect, useState, Fragment, useRef } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import Chip from '@mui/material/Chip';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { Link } from 'react-router-dom';
import { EditComponent } from './EditComponent';
import { CanEdit, DetailPanelEntity, LinkMeta } from './DetailPanel';
import { Text } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import {
  formatPercent,
  formatPrice,
  getFormattedDate,
  isBoolean,
  isCurrency,
  isMonthYearField,
  isPercent,
  isDoubleWide,
  chipStyles,
  CHIP_ELIGIBLE_FIELDS,
  normalizeMultiline
} from '../../../utils/helpers';
import { User } from '../../../store/User';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { financialEditFields } from '../../Pages/Asset/assetHelpers';
import { EditFinancialValuesDialog } from './EditModal/FinancialEditModal';
import { FinancialEditPayload } from './EditModal/financialEditHelpers';
import { Asset, isAsset } from '../../../store/Asset';
import { ProvisionalAsset } from '../../../store/ProvisionalAsset';
import { RpmAccount } from '../../../store/RpmAccount';
import { Link as TallyLink } from '../../../store/Link';
import {
  EditRelationshipModal,
  editRelationshipFields,
  EditRelationshipFieldData
} from './EditModal/RelationshipModal';
import { PaymentFrequencyModal } from './EditModal/PaymentFrequencyModal';
import { CallResult } from '../../../Api/APIService';
import { RecordLink } from '../../Atoms/RecordLink';

export enum EditFieldType {
  SELECT = 'select',
  MULTI_SELECT = 'multi-select',
  TEXT = 'text',
  TEXTAREA = 'text-area',
  DATE = 'date',
  CURRENCY = 'currency',
  PERCENT = 'percent',
  AUTOCOMPLETE = 'autocomplete',
  SEARCHBOX = 'searchbox',
  CHECKBOX = 'checkbox'
}

const defaultContainerClass = 'w-1/2 flex flex-col mb-1 pr-9 pb-4';
const textAreaClass = 'w-full flex flex-col mb-1 pb-6';

const dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z)?$/;

const dateFormat = (field: string) => {
  if (['createdAt', 'beforeSnapshotTimestamp', 'afterSnapshotTimestamp', 'completedAt'].includes(field)) return 'lll';

  return isMonthYearField(field) ? 'MMM YYYY' : undefined;
};
export interface LabelValueFieldParams {
  label: string;
  value: string | number | null;
  field: string;
  saveValueAction?: (update: string | number | null, previewValue?: string) => Promise<string | null>;
  saveObjectAction?: (
    data: Asset | ProvisionalAsset | TallyLink | RpmAccount | FinancialEditPayload
  ) => Promise<CallResult<unknown> | void>;
  canEdit?: CanEdit;
  link?: LinkMeta;
  data?: DetailPanelEntity;
  isValid?: boolean;
}

export const LabelValueField = ({
  label,
  value,
  saveValueAction,
  canEdit,
  link,
  field,
  data,
  isValid = true,
  saveObjectAction
}: LabelValueFieldParams) => {
  const [mode, setMode] = useState<'read' | 'edit'>('read');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [displayValue, setDisplayValue] = useState<string | React.ReactNode>('None');
  const [inputValue, setInputValue] = useState<string | number | null | boolean>(value ?? canEdit?.default);
  const cancelClickedRef = useRef(false);
  const isDoubleWideField = isDoubleWide(field);
  let containerClass = defaultContainerClass;
  if (isDoubleWideField && canEdit && !canEdit.useDefaultWidth) containerClass = `${defaultContainerClass} w-full`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { quoteRepInvolved, quoteTeamSellingDiscount, quoteTeamSellingApprovalNumber } = data || ({} as any);

  const handleBlur = async (newValue: string | number | null, newDisplayValue?: string) => {
    if (cancelClickedRef.current) {
      cancelClickedRef.current = false;
      return;
    }
    setLoading(true);
    setInputValue(newDisplayValue !== undefined ? newDisplayValue : newValue);
    setMode('read');
    if (saveValueAction) {
      const error = await saveValueAction(newValue, newDisplayValue);
      if (error) setInputValue(value ?? canEdit?.default);
    }
    setLoading(false);
  };

  const handleClick = () => {
    if (financialEditFields.includes(field) || editRelationshipFields.includes(field) || field === 'paymentFrequency')
      setModalOpen(true);
    else if (canEdit) setMode(mode === 'read' ? 'edit' : 'read');
  };

  const displayValueWithLink = (val: string | number | null | boolean, link: LinkMeta | undefined, id?: string) => {
    if (link && val && val !== 'None') {
      /** @NOTE Only link to internal Tally page */
      if (!link.external) {
        return (
          <Link
            className="text-indigo"
            title="Fullscreen"
            to={link.link || ''}>
            <div className="flex items-center text-small">{String(val)}</div>
          </Link>
        );
      }

      /** @NOTE Only link to external page */
      if (link.external && !link.link) {
        return (
          <div className="flex justify-start items-center">
            {canEdit ? <div className="hover:bg-gray-100 cursor-pointer">{String(val)}</div> : ''}
            <a
              className="text-indigo"
              title="Fullscreen"
              href={
                (typeof link.externalLink === 'string' ? link.externalLink : link.externalLink?.(id as string)) || ''
              }
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center text-small">
                {canEdit ? '' : String(val)}
                <Tooltip
                  arrow
                  title="Open"
                  placement="bottom">
                  <div data-testid="openInSalesforceButton">
                    <Icon type="external" />
                  </div>
                </Tooltip>
              </div>
            </a>
          </div>
        );
      }

      /** @NOTE Link to both a Tally page and external page */
      if (link.external && link.link) {
        return (
          <div className="flex justify-start">
            <RecordLink
              to={link.link}
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center text-small">{String(val)}</div>
            </RecordLink>
            <Tooltip
              arrow
              title="Open in SF"
              placement="bottom">
              <a
                data-testid="openInSalesforceButton"
                href={
                  (typeof link.externalLink === 'string' ? link.externalLink : link.externalLink?.(id as string)) || ''
                }
                target="_blank"
                rel="noreferrer">
                <Icon type="external" />
              </a>
            </Tooltip>
          </div>
        );
      }
    }

    if (editRelationshipFields.includes(field)) {
      return (
        <div className="flex items-center">
          {String(val) == 'None' && (
            <IconButton
              sx={{ padding: '0 5px 2px 0' }}
              onClick={() => setModalOpen(true)}
              title="Edit Relationship"
              aria-label="Edit Relationship">
              <Icon type="plus" />
            </IconButton>
          )}
          <Text
            className="text-indigo cursor-pointer"
            size="sm14"
            onClick={handleClick}>
            {String(val) !== 'None'
              ? String(val)
              : EditRelationshipFieldData[field as keyof typeof EditRelationshipFieldData].title}
          </Text>
        </div>
      );
    }

    return String(val);
  };

  const formatValue = (val: string | number | null | object[] | User | boolean) => {
    let displayVal;
    if ((!inputValue && inputValue !== 0) || /^\s*$/.test(String(inputValue)) || inputValue === null) val = 'None';
    if (inputValue && dateRegex.test(String(inputValue))) val = getFormattedDate(String(inputValue), dateFormat(field));
    if (isCurrency(field) && (val || val === 0) && val !== 'None') val = formatPrice(parseFloat(String(val)));
    if (isPercent(field) && (val || val === 0) && val !== 'None') val = formatPercent(parseFloat(String(val)));
    if (isBoolean(field)) val = inputValue === true ? 'Yes' : 'No';
    if (field === 'description' && !showMore && typeof val === 'string' && val.length > 100) {
      val = `${val.substring(0, 100)}...`;
    }

    if (val instanceof Array) {
      displayVal = val.map((v) => {
        if (typeof v === 'object') {
          const { name, id, sf_id } = v as { id: string; name: string; sf_id?: string };
          const newLink = link ? { ...link, link: link.link === null ? null : `${link.link}${id}` } : undefined;
          return <Fragment key={id}>{displayValueWithLink(name, newLink, sf_id)}</Fragment>;
        } else {
          return displayValueWithLink(v, link);
        }
      });
    } else {
      if (val instanceof Object) val = `${(val as User).firstName} ${(val as User).lastName}`;
      if (typeof val === 'string' && !!val) val = normalizeMultiline(val);
      displayVal = displayValueWithLink(val, link);
    }

    return displayVal;
  };

  const handleCopyApprovalNumber = async () => {
    await navigator.clipboard.writeText(quoteTeamSellingApprovalNumber || '');
  };

  const isShowTeamSellingDiscount = field === 'commissionableMRC' && quoteRepInvolved;

  useEffect(() => {
    setDisplayValue(formatValue(inputValue));
  }, [inputValue, showMore]);

  useEffect(() => {
    setInputValue(value ?? canEdit?.default);
  }, [value]);

  return (
    <>
      {label ? (
        <div
          style={{ padding: '0 0.5rem 0.75rem 0' }}
          className={`${
            canEdit?.type === EditFieldType.TEXTAREA && !canEdit.useDefaultWidth ? textAreaClass : containerClass
          }`}
          data-testid="labelValueField-container">
          <Text
            dataTestId={`${label}-label-field`}
            className={!isValid ? 'font-semibold' : ''}
            size="sm14"
            color={isValid ? 'grey6' : 'orange'}>
            {label}
          </Text>
          {loading && (
            <ComponentSpinner
              label="Saving"
              sx={{ display: 'flex' }}
              className="None"
              size={15}
            />
          )}
          {!loading && mode === 'read' && (
            <>
              <div className={`${isShowTeamSellingDiscount && 'flex'}`}>
                <Text
                  className={`whitespace-pre-line ${(canEdit && canEdit.type === EditFieldType.TEXTAREA) || isDoubleWideField ? 'w-full' : 'w-10/12'} ${
                    canEdit && 'hover:bg-gray-100 hover:rounded'
                  } ${isShowTeamSellingDiscount && 'w-[74px]'}`}
                  dataTestId={`${label}-read-only-field`}
                  onClick={handleClick}
                  size="sm14"
                  color={
                    displayValue === 'None'
                      ? 'grey3'
                      : (isCurrency(field) || isPercent(field)) && String(displayValue || '').includes('(')
                        ? 'priceRed'
                        : 'grey9'
                  }>
                  {CHIP_ELIGIBLE_FIELDS.includes(field) && !!displayValue ? (
                    <Chip
                      label={displayValue}
                      size="small"
                      sx={chipStyles(displayValue as string)}
                    />
                  ) : (
                    displayValue
                  )}
                </Text>
                {isShowTeamSellingDiscount && (
                  <Tooltip
                    arrow
                    title={`Team Selling Approval Number (click to copy)\n${quoteTeamSellingApprovalNumber}`}
                    placement="bottom"
                    slotProps={{
                      tooltip: {
                        sx: {
                          whiteSpace: 'pre-line'
                        }
                      },
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -9]
                            }
                          }
                        ]
                      }
                    }}>
                    <button
                      onClick={handleCopyApprovalNumber}
                      className="flex items-center text-sm text-black">
                      <Icon
                        type="infoCircleBlue"
                        className="mr-1"
                      />
                      {formatPercent(quoteTeamSellingDiscount || 0)} Discount
                    </button>
                  </Tooltip>
                )}
              </div>
              {field === 'description' && !showMore && value && value.toString().length > 100 && (
                <Text
                  className="text-indigo cursor-pointer"
                  size="sm14"
                  onClick={() => setShowMore(true)}>
                  Show More
                </Text>
              )}
            </>
          )}
          {!loading && canEdit && mode === 'edit' && (
            <div
              data-testid="edit-component-container"
              className="flex items-center space-x-1">
              <EditComponent
                type={canEdit.type}
                setMode={setMode}
                inputMeta={{
                  val: typeof inputValue === 'string' ? normalizeMultiline(inputValue) : inputValue,
                  change: (v) => setInputValue(v),
                  label,
                  handler: handleBlur,
                  options: canEdit.options,
                  mapType: canEdit.mapType
                }}
              />
              <div className="flex items-center flex-col gap-6">
                {canEdit.type === EditFieldType.TEXTAREA && (
                  <IconButton
                    sx={{ padding: '0 5px 0 0' }}
                    onClick={() => {
                      handleBlur(inputValue as string | null);
                    }}
                    title="Save"
                    aria-label="Save">
                    <SaveIcon sx={{ color: 'primary.main' }} />
                  </IconButton>
                )}
                <IconButton
                  sx={{ padding: '0 5px 0 0' }}
                  onMouseDown={() => {
                    cancelClickedRef.current = true;
                  }}
                  onClick={() => {
                    setMode('read');
                    setInputValue(value);
                  }}
                  title="Cancel"
                  aria-label="Cancel">
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="w-1/2 flex flex-col mb-1 pr-9 pb-4"></div>
      )}
      {financialEditFields.includes(field) && (
        <EditFinancialValuesDialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          data={data as Asset}
          onSave={saveObjectAction}
          type={isAsset(data) ? 'asset' : 'provisionalAsset'}
        />
      )}
      {editRelationshipFields.includes(field) && (
        <EditRelationshipModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          field={field}
          data={data as RpmAccount}
          onSave={saveObjectAction}
        />
      )}
      {field === 'paymentFrequency' && (
        <PaymentFrequencyModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          data={data as Asset | ProvisionalAsset}
          onSave={saveObjectAction}
        />
      )}
    </>
  );
};
